import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Member.css';
import { phoneInputProps, PhoneHint } from '../../utils/phone';

const Member = () => {
    const { updateUser } = useAuth();
    const { t } = useLanguage();
    const [memberData, setMemberData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState('personal');
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        emergency_contact: '',
        emergency_phone: ''
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const response = await api.getMyMember();
            if (response && response.data) {
                const memberInfo = response.data.member || response.data;
                setMemberData(memberInfo);
                setFormData({
                    first_name: memberInfo.first_name || '',
                    middle_name: memberInfo.middle_name || '',
                    last_name: memberInfo.last_name || '',
                    email: memberInfo.email || '',
                    phone: memberInfo.phone || '',
                    address: memberInfo.address || '',
                    emergency_contact: memberInfo.emergency_contact || '',
                    emergency_phone: memberInfo.emergency_phone || ''
                });
            }
        } catch (error) {
            console.error('Failed to load profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoClick = () => {
        fileInputRef.current?.click();
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert(t('Tafadhali chagua picha sahihi (JPG, PNG, etc.)'));
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert(t('Picha ni kubwa sana. Ukubwa wa juu ni 5MB'));
            return;
        }

        try {
            setUploadingPhoto(true);
            const response = await api.uploadProfilePicture(file);
            if (response && response.data) {
                setMemberData(prev => ({ ...prev, photo: response.data.photo }));
                updateUser({ photo: response.data.photo });
                alert(t('Picha imebadilishwa!'));
                loadProfile();
            }
        } catch (error) {
            console.error('Failed to upload photo:', error);
            alert(t('Hitilafu imetokea kupakia picha'));
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.phone?.trim() || !formData.email?.trim()) {
            alert(t('Tafadhali jaza vipu vyote vya lazima'));
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert(t('Tafadhali ingiza email sahihi (mfano: example@domain.com)'));
            return;
        }

        try {
            const response = await api.updateMemberProfile(formData);
            if (response && response.data) {
                setMemberData(response.data);
                setEditMode(false);
                alert(t('Taarifa zimebadilishwa!'));
                loadProfile();
            }
        } catch (error) {
            console.error('Failed to update profile:', error);
            alert(error.message || t('Hitilafu imetokea kubadilisha taarifa'));
        }
    };

    const getInitials = (first, last) => {
        const f = (first || 'U')[0].toUpperCase();
        const l = (last || 'U')[0].toUpperCase();
        return f + l;
    };

    if (loading) {
        return (
            <div className="member-page">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>{t('Inapakia...')}</p>
                </div>
            </div>
        );
    }

    if (!memberData) {
        return (
            <div className="member-page">
                <div className="error-state">
                    <p>{t('Hitilafu imetokea kupakia profaili')}</p>
                </div>
            </div>
        );
    }

    const profilePicUrl = memberData?.photo
        ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/uploads/members/${memberData.photo}`
        : null;

    return (
        <div className="member-page">
            <div className="member-header">
                <h1 className="page-title">{t('Wasifu Wangu')}</h1>
                <p className="page-subtitle">{t('Angalia na hariri taarifa zako za kibinafsi')}</p>
            </div>

            {/* Photo Section */}
            <div className="member-photo-section">
                <div className="photo-container">
                    {profilePicUrl ? (
                        <img src={profilePicUrl} alt="Profile" className="member-photo" />
                    ) : (
                        <div className="photo-placeholder">
                            {getInitials(memberData.first_name, memberData.last_name)}
                        </div>
                    )}
                    <button
                        type="button"
                        className="photo-upload-btn"
                        onClick={handlePhotoClick}
                        disabled={uploadingPhoto}
                    >
                        {uploadingPhoto ? '⏳' : '📷'}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                    />
                </div>
                <div className="member-info">
                    <h2>{memberData.first_name} {memberData.last_name}</h2>
                    <p className="member-number">{t('Namba:')} {memberData.member_number}</p>
                    <p className="member-status">{t('Status:')} <span className="badge">{memberData.membership_status}</span></p>
                </div>
            </div>

            {/* Tabs */}
            <div className="member-tabs">
                <button
                    className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                    onClick={() => setActiveTab('personal')}
                >
                    {t('Taarifa ya Kibinafsi')}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                    onClick={() => setActiveTab('contact')}
                >
                    {t('Mawasiliano')}
                </button>
            </div>

            {/* Edit Form */}
            {editMode ? (
                <form onSubmit={handleSubmit} className="member-form">
                    {activeTab === 'personal' && (
                        <div className="form-section">
                            <h3>{t('Taarifa ya Kibinafsi')}</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Jina la Kwanza *')}</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className="form-input"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('Jina la Pili (Optional)')}</label>
                                    <input
                                        type="text"
                                        name="middle_name"
                                        value={formData.middle_name}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('Jina la Ukoo *')}</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'contact' && (
                        <div className="form-section">
                            <h3>{t('Taarifa za Mawasiliano')}</h3>

                            <div className="form-group">
                                <label>{t('Email *')}</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('Simu *')}</label>
                                <input
                                    {...phoneInputProps}
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="form-input"
                                    required
                                />
                                <PhoneHint value={formData.phone} t={t} />
                            </div>

                            <div className="form-group">
                                <label>{t('Anwani')}</label>
                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    className="form-input"
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('Mgeni wa Dharura')}</label>
                                <input
                                    type="text"
                                    name="emergency_contact"
                                    value={formData.emergency_contact}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('Simu ya Dharura')}</label>
                                <input
                                    {...phoneInputProps}
                                    name="emergency_phone"
                                    value={formData.emergency_phone}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                                <PhoneHint value={formData.emergency_phone} t={t} />
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="submit" className="btn btn-primary">{t('Hifadhi')}</button>
                        <button type="button" className="btn btn-secondary" onClick={() => setEditMode(false)}>{t('Ghairi')}</button>
                    </div>
                </form>
            ) : (
                <div className="member-view">
                    {activeTab === 'personal' && (
                        <div className="view-section">
                            <h3>{t('Taarifa ya Kibinafsi')}</h3>
                            <div className="info-row">
                                <span className="label">{t('Jina la Kwanza:')}</span>
                                <span className="value">{memberData.first_name}</span>
                            </div>
                            {memberData.middle_name && (
                                <div className="info-row">
                                    <span className="label">{t('Jina la Pili:')}</span>
                                    <span className="value">{memberData.middle_name}</span>
                                </div>
                            )}
                            <div className="info-row">
                                <span className="label">{t('Jina la Ukoo:')}</span>
                                <span className="value">{memberData.last_name}</span>
                            </div>
                        </div>
                    )}

                    {activeTab === 'contact' && (
                        <div className="view-section">
                            <h3>{t('Taarifa za Mawasiliano')}</h3>
                            <div className="info-row">
                                <span className="label">{t('Email:')}</span>
                                <span className="value">{memberData.email}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">{t('Simu:')}</span>
                                <span className="value">{memberData.phone}</span>
                            </div>
                            {memberData.address && (
                                <div className="info-row">
                                    <span className="label">{t('Anwani:')}</span>
                                    <span className="value">{memberData.address}</span>
                                </div>
                            )}
                            {memberData.emergency_contact && (
                                <div className="info-row">
                                    <span className="label">{t('Mgeni wa Dharura:')}</span>
                                    <span className="value">{memberData.emergency_contact}</span>
                                </div>
                            )}
                            {memberData.emergency_phone && (
                                <div className="info-row">
                                    <span className="label">{t('Simu ya Dharura:')}</span>
                                    <span className="value">{memberData.emergency_phone}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="view-actions">
                        <button className="btn btn-primary" onClick={() => setEditMode(true)}>{t('Hariri')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Member;
